import { Tooltip } from 'antd';
import React from 'react';

import type { HealthStatus } from '@/api/portfolio/api';

const HEALTH_LABEL: Record<HealthStatus, string> = {
  green: 'On track',
  yellow: 'Watching',
  red: 'At risk',
  pending: 'Pending — no update filed',
};

/**
 * Shape + glyph together are the secondary (non-color) encoding so status never
 * relies on hue alone: green = circle "G", yellow = diamond "Y", red = triangle
 * "R", pending = dashed circle "–".
 */
const HEALTH_GLYPH: Record<HealthStatus, string> = {
  green: 'G',
  yellow: 'Y',
  red: 'R',
  pending: '–',
};

/** The three health values the RYG filters/pills expose (no `pending`). */
export type HealthColor = Exclude<HealthStatus, 'pending'>;

/** Value a Health filter dropdown can hold: a RYG color or the `all` reset. */
export type HealthFilterValue = HealthColor | 'all';

export const HEALTH_COLOR_LABEL: Record<HealthColor, string> = {
  green: 'Green',
  yellow: 'Yellow',
  red: 'Red',
};

/**
 * Small shape-coded status marker (green circle / yellow diamond / red circle / pending dashed circle),
 * doubling as the "symbol showing in front" for the stat-card pills, Health
 * filter dropdown options and active-filter chips. Decorative only — the
 * adjacent text label carries the meaning, so status never relies on hue alone.
 */
export const HealthDot: React.FC<{ status: HealthStatus }> = ({ status }) => (
  <span
    className={`health-dot health-dot--${status} pf-health-dot pf-health-dot--${status}`}
    aria-hidden="true"
  />
);

/** Renders "● Green" for a color, or plain "All" for the reset option. */
export const HealthOptionLabel: React.FC<{ value: HealthFilterValue }> = ({ value }) => {
  if (value === 'all') {
    return <span className="health-opt">All</span>;
  }
  return (
    <span className="health-opt">
      <HealthDot status={value} /> {HEALTH_COLOR_LABEL[value]}
    </span>
  );
};

/** The four options every Health filter dropdown offers: All, Green, Yellow, Red. */
export const HEALTH_FILTER_OPTIONS: { value: HealthFilterValue; label: React.ReactNode }[] = (
  ['all', 'green', 'yellow', 'red'] as HealthFilterValue[]
).map((value) => ({ value, label: <HealthOptionLabel value={value} /> }));

export interface HealthIndicatorProps {
  status: HealthStatus;
  /** Optional label prefix, e.g. "On Schedule" → "On Schedule: On track". */
  context?: string;
  /** Hide the glyph (G/Y) and show only the shape (circle/diamond/triangle). */
  hideGlyph?: boolean;
}

/** Small R/Y/G/Pending status glyph driven by theme status tokens. */
export const HealthIndicator: React.FC<HealthIndicatorProps> = ({ status, context, hideGlyph }) => {
  const label = context ? `${context}: ${HEALTH_LABEL[status]}` : HEALTH_LABEL[status];
  return (
    <Tooltip title={label}>
      <span
        className={`health-indicator health-indicator--${status} pf-health pf-health--${status}`}
        role="img"
        aria-label={label}
      >
        {!hideGlyph && HEALTH_GLYPH[status]}
      </span>
    </Tooltip>
  );
};

/** Amber ⚠ badge for projects whose weekly update is overdue (Story 4). */
export const MissingUpdateBadge: React.FC = () => (
  <Tooltip title="Weekly update older than 7 days">
    <span
      className="missing-update-badge pf-missing-badge"
      role="img"
      aria-label="Missing weekly update"
    >
      ⚠
    </span>
  </Tooltip>
);

export default HealthIndicator;
